/**
 * NavbarShell - Unified Client Component Navbar (Unicorn Standard)
 *
 * Diamond Architecture: Client Component for scroll/mobile state.
 * Single component handles all navbar variants across routes.
 *
 * Features:
 * - Scroll-aware shadow/border (solid variant only)
 * - Mobile menu state (future)
 * - Portal slot for dockable search (marketing)
 * - Children slot for section-specific content (browse)
 * - Variant prop for transparent/solid styling
 *
 * Layout:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  LEFT               │      CENTER        │        RIGHT        │
 * │  🏠 Logo + NavLinks │ {children/portal}  │ List + 🌐 + 👤 Menu │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * Usage:
 *   (marketing): <NavbarShell variant="transparent" /> - with portal slot
 *   (browse)/listings: <NavbarShell><ListingsSearchBar /></NavbarShell>
 *   (browse)/estates: <NavbarShell><EstatesSearchBar /></NavbarShell>
 *   detail pages: <NavbarShell /> - clean navbar, no center content
 */
import { AppLogo } from '@/shared/components/app';
import { Link } from '@/shared/i18n/routing';
import { routes } from '@letsgethome/routes';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { useSyncExternalStore } from 'react';

import { AccountMenu } from './AccountMenu';
import { ListPropertyButton } from './ListPropertyButton';
import { NavLinksClient, type SectionLink } from './NavLinksClient';
import { PreferencesModal } from './PreferencesModal';

export type NavbarVariant = 'solid' | 'transparent';

interface NavbarShellProps {
  /** Visual variant - 'solid' has bg, 'transparent' is clear (for hero pages) */
  variant?: NavbarVariant;
  /** Center slot content - search bars, filters, etc. */
  children?: React.ReactNode;
  /** Enable portal slot for dockable search (marketing pages) */
  enablePortalSlot?: boolean;
  /** Optional section link (e.g., Blog, Estates) - appears after Rent/Buy */
  sectionLink?: SectionLink;
}

function subscribeToScroll(onStoreChange: () => void) {
  window.addEventListener('scroll', onStoreChange, { passive: true });
  return () => window.removeEventListener('scroll', onStoreChange);
}
const getIsScrolled = () => window.scrollY > 0;
const getServerIsScrolled = () => false;

export function NavbarShell({
  variant = 'solid',
  children,
  enablePortalSlot = false,
  sectionLink,
}: NavbarShellProps) {
  // The scroll position is a client-only store: the server renders "not
  // scrolled" and the hydration pass MUST reproduce that, so the live value is
  // read through `useSyncExternalStore` with a server snapshot rather than a
  // `useState` seed from `window.scrollY` (the LET-87 class — the old seed
  // mismatched on every reload that restored a scroll offset).
  const isScrolled = useSyncExternalStore(subscribeToScroll, getIsScrolled, getServerIsScrolled);

  // Determine effective variant based on scroll
  // Transparent becomes solid when scrolled
  const effectiveVariant = variant === 'transparent' && !isScrolled ? 'transparent' : 'solid';

  return (
    <header
      className={cn(
        'sticky top-0 hidden transition-all duration-200 md:block',
        Z_LAYERS.navbar.className,
        // Height for CSS calc() in DockableHeroSearch
        'h-[var(--navbar-height)]',
        // Variant styles
        effectiveVariant === 'solid' && 'bg-background',
        effectiveVariant === 'transparent' && 'bg-transparent',
        // Border/shadow on scroll (solid only)
        effectiveVariant === 'solid' && isScrolled && 'border-b border-border shadow-sm',
        effectiveVariant === 'solid' && !isScrolled && 'border-b border-transparent'
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        {/* LEFT: Logo + Nav Links */}
        <div className="flex items-center gap-6">
          <Link href={routes.home()} prefetch="intent" className="shrink-0">
            <AppLogo />
          </Link>
          <NavLinksClient sectionLink={sectionLink} />
        </div>

        {/* CENTER: Portal slot OR children */}
        <div className="flex flex-1 items-center justify-center px-8">
          {enablePortalSlot ? (
            // Portal target for DockableHeroSearch
            <div id="navbar-portal-slot" className="flex items-center justify-center" />
          ) : (
            // Direct children (search bars, etc.)
            children
          )}
        </div>

        {/* RIGHT: List Property + Language + Account */}
        <div className="flex items-center gap-2">
          <ListPropertyButton />
          <PreferencesModal />
          <AccountMenu />
        </div>
      </div>
    </header>
  );
}
